/**
 * BNI Offer Landing — behavior
 * ---------------------------------------------------------------------
 * - Smooth-scrolls from hero CTA to the form.
 * - Validates the verification form inline (no page reload).
 * - Submits the lead to a Google Apps Script Web App via submitLead(),
 *   which POSTs to the URL configured in Theme Editor (data-gas-url on
 *   the section root). The Apps Script appends/updates a row in Google
 *   Sheets and returns JSON — see google-apps-script/Code.gs.
 * - No Shopify Customer, Admin API, App Proxy, or Node backend involved.
 * - Remembers "unlocked" state per-browser in localStorage so a repeat
 *   visitor sees their offers immediately, without resubmitting.
 * - Handles per-offer-card "Copy Coupon" buttons.
 *
 * No external libraries. No jQuery.
 * ---------------------------------------------------------------------
 */
(function () {
  'use strict';

  var VALIDATORS = {
    first_name: function (value) {
      return value.trim().length >= 1 ? '' : 'Please enter your first name.';
    },
    last_name: function (value) {
      return value.trim().length >= 1 ? '' : 'Please enter your last name.';
    },
    email: function (value) {
      var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(value.trim()) ? '' : 'Please enter a valid email address.';
    },
    mobile: function (value) {
      var digits = value.replace(/[\s\-()]/g, '');
      var re = /^\+?\d{10,13}$/;
      return re.test(digits) ? '' : 'Please enter a valid mobile number.';
    },
    business_name: function (value) {
      return value.trim().length >= 2 ? '' : 'Please enter your business name.';
    },
    bni_chapter: function (value) {
      return value.trim().length >= 2 ? '' : 'Please enter your BNI chapter.';
    }
  };

  /**
   * =========================================================================
   * submitLead() — real backend call (Google Apps Script + Google Sheets)
   * =========================================================================
   * Replaces the earlier createCustomer() placeholder. No Shopify Customer,
   * Admin API, App Proxy, or Node backend is used. The lead is sent directly
   * to a Google Apps Script Web App URL (configured in Theme Editor →
   * "Lead capture backend" → Google Apps Script Web App URL), which writes
   * a row into Google Sheets. See google-apps-script/Code.gs for the
   * server-side implementation and google-apps-script/README.md for the
   * deployment guide.
   *
   * IMPLEMENTATION NOTE — why Content-Type is 'text/plain':
   * Google Apps Script Web Apps don't handle CORS preflight (OPTIONS)
   * requests. Sending 'Content-Type: application/json' triggers a
   * preflight in the browser, which Apps Script can't answer, and the
   * request fails. Sending 'text/plain' keeps this a CORS "simple
   * request" (no preflight), and Apps Script's doPost(e) can still read
   * e.postData.contents and JSON.parse it normally server-side — the
   * body itself is still valid JSON, only the declared Content-Type
   * differs. This is the standard, documented workaround for this exact
   * scenario.
   *
   * Returns a Promise resolving to { ok: boolean, message?: string,
   * leadId?: string, duplicate?: boolean }.
   * =========================================================================
   */
  function submitLead(gasUrl, payload) {
    if (!gasUrl) {
      return Promise.resolve({
        ok: false,
        message: 'This form is not fully configured yet. Please contact the site owner.'
      });
    }

    return fetch(gasUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            // Apps Script returned something that wasn't valid JSON
            // (misconfigured deployment, HTML error page, etc.)
            throw new Error('invalid-response');
          });
      })
      .then(function (data) {
        if (data && data.success) {
          return { ok: true, leadId: data.leadId, duplicate: !!data.duplicate };
        }
        return {
          ok: false,
          message: (data && data.message) || 'Something went wrong. Please try again.'
        };
      })
      .catch(function () {
        return {
          ok: false,
          message: 'Network error — please check your connection and try again.'
        };
      });
  }

  /**
   * Gathers the automatic hidden fields that get sent with every
   * submission alongside the visible form fields. None of these are
   * asked of the visitor.
   */
  function collectAutomaticFields(root) {
    var now = new Date();

    function pad(n) {
      return String(n).padStart(2, '0');
    }

    var submissionDate = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    var submissionTime = pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':' + pad(now.getSeconds());

    var timezone = '';
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      timezone = '';
    }

    return {
      campaign_id: root.dataset.campaignId || '',
      campaign_name: root.dataset.campaignName || '',
      campaign_source: root.dataset.campaignSource || '',
      lead_source: root.dataset.leadSource || '',
      landing_page_url: window.location.href,
      website_domain: window.location.hostname,
      submission_date: submissionDate,
      submission_time: submissionTime,
      iso_timestamp: now.toISOString(),
      user_agent: navigator.userAgent || '',
      browser_language: navigator.language || '',
      screen_resolution: window.screen ? window.screen.width + 'x' + window.screen.height : '',
      timezone: timezone,
      referrer: document.referrer || ''
    };
  }

  function initSection(root) {
    var storageKey = root.dataset.storageKey;
    var gasUrl = root.dataset.gasUrl;

    var formCard = root.querySelector('[data-bni-state="form"]');
    var successCard = root.querySelector('[data-bni-state="success"]');
    var form = root.querySelector('.bni-offer__form');
    var statusEl = root.querySelector('.bni-offer__form-status');
    var submitBtn = form ? form.querySelector('.bni-offer__submit') : null;
    var scrollTrigger = root.querySelector('[data-bni-scroll-to]');

    /* ---------- returning-visitor check ---------- */
    function isAlreadyUnlocked() {
      try {
        return window.localStorage.getItem(storageKey) === '1';
      } catch (e) {
        // localStorage unavailable (private browsing, etc.) — degrade gracefully.
        return false;
      }
    }

    function markUnlocked() {
      try {
        window.localStorage.setItem(storageKey, '1');
      } catch (e) {
        /* no-op */
      }
    }

    function revealSuccess() {
      if (formCard) formCard.hidden = true;
      if (successCard) successCard.hidden = false;
      if (successCard) successCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (isAlreadyUnlocked()) {
      revealSuccess();
    }

    /* ---------- smooth scroll from hero CTA ---------- */
    if (scrollTrigger) {
      scrollTrigger.addEventListener('click', function () {
        var targetSelector = scrollTrigger.getAttribute('data-bni-scroll-to');
        var target = targetSelector ? document.querySelector(targetSelector) : null;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          var firstInput = target.querySelector('input');
          if (firstInput) {
            window.setTimeout(function () {
              firstInput.focus({ preventScroll: true });
            }, 500);
          }
        }
      });
    }

    /* ---------- inline validation ---------- */
    function setFieldError(field, message) {
      var errorEl = document.getElementById(field.getAttribute('aria-describedby'));
      if (errorEl) errorEl.textContent = message;
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    function validateField(field) {
      var validator = VALIDATORS[field.name];
      if (!validator) return true;
      var message = validator(field.value || '');
      setFieldError(field, message);
      return message === '';
    }

    function validateForm() {
      var valid = true;
      Object.keys(VALIDATORS).forEach(function (name) {
        var field = form.elements[name];
        if (field && !validateField(field)) valid = false;
      });
      return valid;
    }

    if (form) {
      Object.keys(VALIDATORS).forEach(function (name) {
        var field = form.elements[name];
        if (!field) return;
        field.addEventListener('blur', function () {
          validateField(field);
        });
        field.addEventListener('input', function () {
          if (field.getAttribute('aria-invalid') === 'true') {
            validateField(field);
          }
        });
      });
    }

    /* ---------- submit ---------- */
    function setStatus(message, tone) {
      if (!statusEl) return;
      statusEl.textContent = message || '';
      if (tone) {
        statusEl.setAttribute('data-tone', tone);
      } else {
        statusEl.removeAttribute('data-tone');
      }
    }

    function setSubmitting(isSubmitting) {
      if (!submitBtn) return;
      submitBtn.disabled = isSubmitting;
      submitBtn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }

    function handleSubmit(event) {
      event.preventDefault();
      setStatus('');

      if (!validateForm()) {
        setStatus('Please fix the highlighted fields.', 'error');
        var firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      // Visible fields the visitor typed, plus every automatic hidden
      // field — merged into one payload sent to Google Apps Script.
      var payload = Object.assign(
        {
          first_name: form.elements.first_name.value.trim(),
          last_name: form.elements.last_name.value.trim(),
          email: form.elements.email.value.trim(),
          mobile: form.elements.mobile.value.trim(),
          business_name: form.elements.business_name.value.trim(),
          bni_chapter: form.elements.bni_chapter.value.trim()
        },
        collectAutomaticFields(root)
      );

      setSubmitting(true);
      setStatus('Verifying your details…');

      // On failure: field values are left exactly as the visitor typed
      // them (never reset), and the submit button re-enables so they
      // can simply retry.
      submitLead(gasUrl, payload)
        .then(function (result) {
          setSubmitting(false);
          if (result && result.ok) {
            markUnlocked();
            setStatus('');
            revealSuccess();
          } else {
            var message = (result && result.message) || 'Something went wrong. Please try again.';
            setStatus(message, 'error');
          }
        })
        .catch(function () {
          setSubmitting(false);
          setStatus('Something went wrong. Please try again.', 'error');
        });
    }

    if (form) {
      form.addEventListener('submit', handleSubmit);
    }

    /* ---------- copy coupon (per offer card) ---------- */
    // Event delegation: there can be several offer cards, each with its
    // own Copy Coupon button and its own coupon code / confirmation text.
    root.addEventListener('click', function (event) {
      var copyBtn = event.target.closest('[data-bni-copy]');
      if (!copyBtn || !root.contains(copyBtn)) return;

      var card = copyBtn.closest('.bni-offer__offer-card') || root;
      var code =
        copyBtn.getAttribute('data-bni-copy-code') ||
        (card.querySelector('[data-bni-coupon-code]') || {}).textContent;
      if (!code) return;

      var copyConfirm = card.querySelector('[data-bni-copy-confirm]');
      var copyLabel = copyBtn.querySelector('[data-bni-copy-label]');
      var originalLabel = copyLabel ? copyLabel.textContent : '';

      var writeToClipboard = navigator.clipboard && navigator.clipboard.writeText
        ? navigator.clipboard.writeText(code)
        : legacyCopy(code);

      Promise.resolve(writeToClipboard)
        .then(function () {
          if (copyConfirm) copyConfirm.textContent = 'Coupon Copied!';
          if (copyLabel) copyLabel.textContent = 'Copied';
          window.setTimeout(function () {
            if (copyConfirm) copyConfirm.textContent = '';
            if (copyLabel) copyLabel.textContent = originalLabel || 'Copy Coupon';
          }, 2200);
        })
        .catch(function () {
          if (copyConfirm) copyConfirm.textContent = 'Could not copy — please copy manually.';
        });
    });

    function legacyCopy(text) {
      return new Promise(function (resolve, reject) {
        var textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          resolve();
        } catch (err) {
          reject(err);
        } finally {
          document.body.removeChild(textarea);
        }
      });
    }
  }

  function init() {
    var sections = document.querySelectorAll('[data-bni-offer]');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if the section is added/edited live in the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-bni-offer]');
    if (root) initSection(root);
  });
})();
